import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { api } from "../lib/api";
import { buildWhatsAppUrl, buildTelUrl } from "../lib/whatsapp";
import { CheckCircle2, MessageCircle, Loader2, Phone, FileText } from "lucide-react";

export const EnquiryDialog = ({ product, open, onOpenChange, whatsappNumber, googleFormUrl }) => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", city: "", extra: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);

  const reset = () => {
    setForm({ name: "", phone: "", email: "", city: "", extra: "", notes: "" });
    setSuccess(null);
    setLoading(false);
  };

  const handleClose = (next) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const buildWhatsAppLink = () => {
    const lines = [
      `Hi MY BEEMA, I'd like a quote for *${product.title}*.`,
      `Name: ${form.name}`,
      `Phone: ${form.phone}`,
      form.email && `Email: ${form.email}`,
      form.city && `City: ${form.city}`,
      form.extra && `${product.extraLabel}: ${form.extra}`,
      form.notes && `Notes: ${form.notes}`,
    ].filter(Boolean);
    return buildWhatsAppUrl(whatsappNumber, lines.join("\n"));
  };

  const submit = async (e) => {
    e.preventDefault();
    const phoneClean = form.phone.replace(/[\s-]/g, "");
    if (!form.name.trim()) {
      toast.error("Please enter your name.");
      return;
    }
    if (!/^[6-9]\d{9}$/.test(phoneClean)) {
      toast.error("Enter a valid 10-digit Indian mobile number (starting 6-9).");
      return;
    }
    setLoading(true);
    try {
      await api.post("/enquiries", {
        product_id: product.id,
        product_title: product.title,
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        city: form.city.trim(),
        extra: form.extra.trim(),
        notes: form.notes.trim(),
      });
      const link = buildWhatsAppLink();
      setSuccess({ link });
      toast.success("Enquiry received. We'll reach out shortly.");
    } catch (err) {
      toast.error("Couldn't submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!product) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg backdrop-blur-xl bg-white/95 border border-white/20 shadow-2xl rounded-2xl" data-testid="enquiry-dialog">
        {!success ? (
          <>
            <DialogHeader>
              <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#006A4E]">Get a Quote</div>
              <DialogTitle className="font-display text-2xl md:text-3xl text-[#0B192C]">{product.title}</DialogTitle>
              <DialogDescription className="text-[#4A5568]">
                Share a few details — our advisor will reach out within 24 hours.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={submit} className="space-y-4 mt-2">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name" className="text-xs uppercase tracking-wider text-[#0B192C]">Full Name *</Label>
                  <Input id="name" data-testid="enquiry-input-name" required value={form.name} onChange={update("name")} className="mt-1.5 rounded-lg" placeholder="Your name" />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-xs uppercase tracking-wider text-[#0B192C]">Phone *</Label>
                  <Input id="phone" data-testid="enquiry-input-phone" required value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value.replace(/[^\d]/g, "").slice(0, 10) }))} className="mt-1.5 rounded-lg" placeholder="10-digit mobile" maxLength={10} inputMode="numeric" pattern="[6-9][0-9]{9}" />
                </div>
                <div>
                  <Label htmlFor="email" className="text-xs uppercase tracking-wider text-[#0B192C]">Email</Label>
                  <Input id="email" data-testid="enquiry-input-email" type="email" value={form.email} onChange={update("email")} className="mt-1.5 rounded-lg" placeholder="you@example.com" />
                </div>
                <div>
                  <Label htmlFor="city" className="text-xs uppercase tracking-wider text-[#0B192C]">City</Label>
                  <Input id="city" data-testid="enquiry-input-city" value={form.city} onChange={update("city")} className="mt-1.5 rounded-lg" placeholder="e.g. Delhi" />
                </div>
              </div>

              <div>
                <Label htmlFor="extra" className="text-xs uppercase tracking-wider text-[#0B192C]">{product.extraLabel}</Label>
                <Input id="extra" data-testid="enquiry-input-extra" value={form.extra} onChange={update("extra")} className="mt-1.5 rounded-lg" placeholder={product.extraPlaceholder} />
                {product.extraHints && product.extraHints.length > 0 && (
                  <div className="mt-2.5" data-testid="enquiry-extra-hints">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#4A5568] mb-1.5">Common options — tap to add</div>
                    <div className="flex flex-wrap gap-1.5">
                      {product.extraHints.map((hint) => (
                        <button
                          key={hint}
                          type="button"
                          data-testid={`extra-hint-${hint.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          onClick={() => setForm((f) => ({
                            ...f,
                            extra: f.extra
                              ? f.extra.split(",").map((s) => s.trim()).filter(Boolean).includes(hint)
                                ? f.extra
                                : `${f.extra}${f.extra.trim().endsWith(",") ? " " : ", "}${hint}`
                              : hint,
                          }))}
                          className="text-xs px-3 py-1 rounded-full border border-[#E2E8F0] bg-white hover:bg-[#E6F0ED] hover:border-[#006A4E] hover:text-[#006A4E] text-[#4A5568] transition-colors"
                        >
                          {hint}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="notes" className="text-xs uppercase tracking-wider text-[#0B192C]">Anything we should know?</Label>
                <Textarea id="notes" data-testid="enquiry-input-notes" value={form.notes} onChange={update("notes")} className="mt-1.5 rounded-lg" placeholder="Optional notes" rows={2} />
              </div>

              <DialogFooter className="pt-2">
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="enquiry-submit-btn"
                  className="w-full rounded-full bg-[#006A4E] hover:bg-[#00523C] text-white py-6 font-semibold shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all"
                >
                  {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Submitting…</>) : "Submit Enquiry"}
                </Button>
              </DialogFooter>
            </form>
          </>
        ) : (
          <div className="py-6 text-center" data-testid="enquiry-success">
            <div className="mx-auto w-16 h-16 rounded-full bg-[#E6F0ED] flex items-center justify-center mb-4">
              <CheckCircle2 className="w-9 h-9 text-[#006A4E]" strokeWidth={1.5} />
            </div>
            <DialogTitle className="font-display text-2xl text-[#0B192C]">Thank you, {form.name.split(" ")[0]}!</DialogTitle>
            <p className="text-[#4A5568] mt-2 max-w-sm mx-auto">
              Your enquiry for <strong>{product.title}</strong> is received. Continue on WhatsApp for an instant chat with our advisor.
            </p>
            <div className="mt-6 space-y-3">
              <a
                data-testid="enquiry-whatsapp-cta"
                href={success.link}
                target="_blank"
                rel="noreferrer"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1ebe57] text-white py-3 px-6 font-semibold shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all"
              >
                <MessageCircle className="w-5 h-5" /> Get Quote on WhatsApp
              </a>
              <div className="grid grid-cols-2 gap-3">
                <a
                  data-testid="enquiry-call-cta"
                  href={buildTelUrl(whatsappNumber)}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-[#006A4E] text-[#006A4E] hover:bg-[#006A4E] hover:text-white py-3 px-4 font-semibold transition-all text-sm"
                >
                  <Phone className="w-4 h-4" /> Call us
                </a>
                {googleFormUrl ? (
                  <a
                    data-testid="enquiry-gform-cta"
                    href={googleFormUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-[#0B192C]/20 text-[#0B192C] hover:bg-[#0B192C] hover:text-white py-3 px-4 font-semibold transition-all text-sm"
                  >
                    <FileText className="w-4 h-4" /> Google Form
                  </a>
                ) : (
                  <span />
                )}
              </div>
              <p className="text-[11px] text-[#4A5568] text-center pt-1">
                WhatsApp blocked on your network? Use Call or the Google Form instead.
              </p>
              <button
                data-testid="enquiry-close-btn"
                onClick={() => handleClose(false)}
                className="block mx-auto text-sm text-[#4A5568] hover:text-[#0B192C] underline-offset-4 hover:underline"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
